import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import Toast from '../components/toast';
import { isEmpty } from '../components/enums';
import SelectAllControl from '../components/select_all_control';
import NoItemsOverlay from '../components/no_items_overlay';

// Material UI
import {
	DataGrid,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	useGridApiRef,
	gridFilteredSortedRowEntriesSelector,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

function LocationUserManagementToolbar({ onSelectAll, selectAllState }) {
	return (
		<GridToolbarContainer>
			<GridToolbarColumnsButton />
			<GridToolbarFilterButton />
			<SelectAllControl
				onSelectAll={onSelectAll}
				selectAllState={selectAllState}
				columnName='Has Access'
				itemType='users'
			/>
		</GridToolbarContainer>
	);
}

export default function LocationUsersManagement() {

	const { locationNum } = useParams();	
	const [locationInfo, setLocationInfo] = useState({});
	
	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});

	// Collection of Users data. This should only change if requesting user's information changes. This instance would require a page refresh
	const [usersData, setUsersData] = useState([]);
	
	// List of users that have access to the location
	const [locUserPermissions, setLocUserPermissions] = useState([]);
	
	// Data show in the table
	const [tableData, setTableData] = useState([]);
	
	// Permissions management
	const [changesMade, setChangesMade] = useState(false);
	const [userIDsChanged, setUserIDsChanged] = useState([]);

	// The user information for the logged-in user
	const [userInfo, setUserInfo] = useState({});

	const [intLocationNum, setIntLocationNum] = useState(parseInt(locationNum, 10));

	const [selectAllState, setSelectAllState] = useState(true);

	// DataGrid pagination state
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

	const apiRef = useGridApiRef();

	// User List table columns
	// TODO: FIX THE COMMENTED-OUT PERMISSIONS SO THAT ONCE ACCESS IS GRANTED, THEY CAN BE CHANGED WITHOUT PAGE REFRESH
	const [userColumns, setUserColumns] = useState([
		{
			field: 'first_name',
			headerName: 'First Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'First Name',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'last_name',
			headerName: 'Last Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Last Name',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'email',
			headerName: 'Email',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Email Address',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'company',
			headerName: 'Company',
			headerAlign: 'center',
			headerClassName: 'wrap-header-text' ,
			description: 'Name of the Company that the user works for',
			minWidth: 100,
			flex: 1,
		},
		{
			field: 'is_staff',
			headerName: 'Is Admin',
			headerAlign: 'center',
			headerClassName: 'wrap-header-text' ,
			description: 'Is the user an admin? (Can manage users)',
			minWidth: 100,
			flex: 1,
			renderCell: (params) => renderYesNo(params.value),	
		},
		{
			field: 'is_active',
			headerName: 'Enabled',
			headerAlign: "center",
			description: 'Whether or not the user\'s account is enabled',
			minWidth: 100,
			flex: 1,
			renderCell: (params) => renderYesNo(params.value),
		},
		{
			field: 'has_access',
			headerName: 'Has Access',
			headerAlign: "center",
			description: 'Can the User view this location?',
			minWidth: 100,
			flex: 1,
			filterable: false,
			hideable: false,
			renderCell: (params) => {
				// If the user is a superuser, access should not be changeable
				return params.row.is_superuser ?
					renderYesNo(true) :
					renderSwitch(params.row.id, 'has_access', params.value)
			},
		},
		// {
		// 	field: 'can_change_settings',
		// 	headerName: 'System Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s system settings (not including limits)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_settings'),
		// },
		// {
		// 	field: 'can_change_limits',
		// 	headerName: 'Change Limits',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s limit settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_limits'),
		// },
		// {
		// 	field: 'can_change_email_settings',
		// 	headerName: 'Email Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s email settings (for all users)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_email_settings'),
		// },
		// {
		// 	field: 'can_change_observer_settings',
		// 	headerName: 'Observer Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s observer settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_observer_settings'),
		// },
		// {
		// 	field: 'can_view_audit_log',
		// 	headerName: 'View Audit Log',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Audit Log?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_view_audit_log'),
		// },
		// {
		// 	field: 'can_view_notes',
		// 	headerName: 'View Notes',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Notes column across the site?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_view_notes'),
		// },
	]);

	// const renderSwitchFromParams = (params, switchName) => {
	// 	if (params.row.is_superuser) {
	// 		return renderDisabledSwitch(true);
	// 	} else {
	// 		return params.row.has_access ? 
	// 			renderSwitch(params.row.userId, switchName, params.value) :
	// 			renderDisabledSwitch(false);
	// 	}
	// }

	const navigate = useNavigate();

	// Get data about the user on the page
	useEffect(() => {
		axiosInstance.get(`/users/`).then(response => {		
			const userInfo = response.data;

			// Check if the user is a superuser - no editing of superusers access
			if (!userInfo || userInfo.length === 0 || (!userInfo.is_staff && !userInfo.is_superuser)) {
				navigate('/403/', { replace: true });
			}

			setUserInfo(response.data);
			
			// Enable an additional column if the user is a superuser
			if (response.data.is_superuser && userColumns.find(column => column.field === 'is_superuser') == undefined) {
				let newUserColumns = [...userColumns];
				
				newUserColumns.splice(4, 0, {
					field: 'is_superuser',
					headerName: 'Is Superuser',
					headerAlign: "center",
					description: 'Whether or not the user is a superuser',
					minWidth: 100,
					flex: 1,
					renderCell: (params) => renderYesNo(params.value),
				});
				setUserColumns(newUserColumns);
			}
		});
	}, []);

	// Get the updated user information that the logged-in user has access to
	useEffect(() => {
		setIntLocationNum(parseInt(locationNum, 10));

		axiosInstance.get(`/loc/${intLocationNum}/size/`)
			.then(response => {
				setLocationInfo(response.data);
			})
			.catch(error => {
				// If the user does not have access to the location, redirect to the 403 page
				if (error.response && error.response.status === 403) {
					navigate('/403/', { replace: true });
				}

				console.error(error);
				setLocationInfo({});
			});

		UpdateUsersData();
	}, [locationNum]);
	
	// Get the list of users that the admin/su has access to
	function UpdateUsersData() {
		axiosInstance.get(`/users/list/`)
			.then(response => {
				setUsersData(response.data);
				GetUsersAccessData();
			})
			.catch(error => {
				console.error(error);
				setUsersData([]);
			});
	}
	
	// Get the list of users that have access to the chosen location
	function GetUsersAccessData() {
		axiosInstance.get(`/users/locations/${intLocationNum}`)
			.then(response => {
				setLocUserPermissions(response.data);
			})
			.catch(error => {
				console.error(error);
				setLocUserPermissions([]);
			});
	}

	// Get the location information for the table
	useEffect(() => {
		BuildTableData(usersData, locUserPermissions);
	}, [locationNum, usersData, locUserPermissions]);
		
	// Merge the two lists of users to build the table
	function BuildTableData(accessUsersList, locationUsersList) {

		// Compare the two lists to see what the selected user has access to
		const combinedList = accessUsersList.map(user => {

			// Try to find a matching user location permission for each requesting user location permission
			let locationUser = undefined;
			if (locationUsersList && locationUsersList.length > 0) {
				locationUser = locationUsersList.find(locUser => locUser.user_id === user.id);
			}

			// If a matching user is found in locationUsersList, merge the two objects, using the user's permissions and ID
			//   otherwise, if no matching location is found in userLocationsList, create a new one and set all permissions fields to false
			if (locationUser) {
				return {
					...user,
					...locationUser,
					has_access: true,
				};
			} else {
				return {
					...user,
					has_access: false,
					change_system_settings: false,
					change_limits: false,
					change_email_settings: false,
					change_observer_settings: false,
					view_audit_log: false,
					view_notes: false
				};
			}
		});

		setTableData(combinedList);
	}

	// Get user's name and/or email for the page title
	function getPageTitle() {
		if (!isEmpty(locationInfo)) {
			if (locationInfo.location_name == '') {
				return "SN " + locationInfo.serial_number;
			} else {
				return locationInfo.location_name + " (SN " + locationInfo.serial_number + ")";
			}
		}

		return '';
	}

	// Append the location ID to the list of locations that have been updated
	const handleIDsAppend = (newInteger) => {
		setUserIDsChanged(prevState => {
			if (!prevState.includes(newInteger)) {
				return [...prevState, newInteger];
			} else {
				return prevState;
			}
		});
	};

	// Handle the switch change event
	const handleSwitchChange = (userId, switchName, event) => {
		setChangesMade(true);
		handleIDsAppend(userId);

		setTableData(prevTableData => {
			return prevTableData.map(row => {
				if (row.id === userId) {
					return { ...row, [switchName]: event.target.checked };
				} else {
					return row;
				}
			});
		});
	};

	// Handle the Select All button click (works with filtering and pagination)
	const handleSelectAll = () => {
		setChangesMade(true);

		// Filtered and sorted rows from the DataGrid
		const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef)
		const startIndex = paginationModel.page * paginationModel.pageSize;
		const endIndex = startIndex + paginationModel.pageSize;
		const visibleRows = filteredRows.slice(startIndex, endIndex);
		
		const updatedUserIds = visibleRows
			.filter(row => !row.is_superuser)
			.map(row => row.id);

		setUserIDsChanged(updatedUserIds);

		setTableData(prevTableData =>
			prevTableData.map( row => updatedUserIds.includes(row.id) ? { ...row, has_access: selectAllState } : row )
		);

		setSelectAllState(!selectAllState);
	};
	
	const handleSaveClick = () => {
		if (userIDsChanged.length === 0) {
			setChangesMade(false);
			return;
		}

		// Build the list of changed users
		let changedUsers = tableData
			.map(userState => {
				if (userIDsChanged.includes(userState.id)) {
					return ({
						user_id: userState.id,
						has_access: userState.has_access,
						change_system_settings: userState.change_system_settings,
						change_limits: userState.change_limits,
						change_email_settings: userState.change_email_settings,
						change_observer_settings: userState.change_observer_settings,
						view_audit_log: userState.view_audit_log,
						view_notes: userState.view_notes,
					});
				}

				return undefined;
			})
			.filter(Boolean);  // Remove undefined entries
		
		// Post the changes to the backend
		axiosInstance.post(`/users/locations/${intLocationNum}/change/`, changedUsers)
			.then(response => {
				let failedChanges = [];

				if (response.data && response.data.length > 0) {
					response.data.results.forEach(changeResult => {
						// Check if any changes failed
						if (!changeResult.success) {
							const failedUser = usersData.find(user => user.id === changeResult.user_id);
							if (failedUser) {
								failedChanges.push({ email: failedUser.email, reason: changeResult.error });
							}
						}
					});
				}

				// Display a toast message based on the success of permission changes
				if (failedChanges.length > 0) {
					const failedText = failedChanges.map(change => `${change.email}: ${change.reason}`).join('\r\n')
					setToast({severity: 'error', message: `Failed to save changes for the following users: \r\n${failedText}`, open: true });
				} else {
					setToast({ severity: 'success', message: 'User permission changes saved successfully.', open: true });
				}
			})
			.catch(error => {
				console.error(error);
				setToast({ severity: 'error', message: 'An error occurred while saving changes. Please try again.', open: true });
			})
			.finally(() => {		
				setChangesMade(false);
				setUserIDsChanged([]);

				// Update the table data
				GetUsersAccessData();
			});
	};

	// Render a switch for a given user and switchName
	const renderSwitch = (userId, switchName, checked) => {
		return (
			<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Switch checked={checked} onChange={(event) => handleSwitchChange(userId, switchName, event)} color='primary' />
			</div>
		);
	}

	// Render a switch for a given user and switchName
	const renderYesNo = (checked) => {
		return (
			<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<p><strong>{ checked ? 'Yes' : 'No' }</strong></p>
			</div>
		);
	}
	
	return (
		<Paper
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				Editing User Access for {getPageTitle()}
			</Typography>
			<DataGrid
				apiRef={apiRef}
				getRowId={(row) => row.id}
				rows={tableData}
				columns={userColumns}
				pagination
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[5, 10, 25, 50, 100]}
				density='compact'
				slots={{
					toolbar: () => <LocationUserManagementToolbar onSelectAll={handleSelectAll} selectAllState={selectAllState} />,
					noRowsOverlay: NoItemsOverlay,
				}}
				slotProps={{
					noRowsOverlay: { noItemsText: 'No Users' },
				}}
				sx={{
					padding: 2,
				}}
			/>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			{ changesMade && (
				<Paper style={{ padding: 10, margin: 1, border: '1px solid lightgray' }}>
					<Box display="flex" justifyContent="space-between" alignItems="right">
						<Typography variant="body1">You have unsaved changes. Would you like to save?</Typography>
						<Box>
							<Button variant="contained" color="primary" onClick={handleSaveClick}>
								Save
							</Button>
						</Box>
					</Box>
				</Paper>
			)}
		</Paper>
	);
}